import { Injectable } from '@angular/core';
import { Study, StudyExpense } from '@models/study';
import { User } from '@models/user';
import { ExpenseRepository } from '@repositories/admin/expense.repository';
import { AdminStudyServices } from '@services/admin/admin-study.sevice';
import { MdsOptionGeneric } from '@utility/utility';
import {
    EMPTY,
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    catchError,
    filter,
    forkJoin,
    map,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class StudyStateService {
    constructor(
        private studyServices: AdminStudyServices,
        private expenseRepository: ExpenseRepository
    ) {
        this.studyLoadTrigger$
            .pipe(
                switchMap((id) =>
                    this.setStudyAsync(id).pipe(
                        catchError((error) => {
                            console.error(`Unable to load study state for id ${id}`, error);
                            return EMPTY;
                        })
                    )
                )
            )
            .subscribe();
    }

    private static _staticStudy: Study;
    public static get staticStudy() {
        return StudyStateService._staticStudy;
    }

    private readonly study = new ReplaySubject<Study>(1);
    public readonly study$ = this.study.asObservable();

    public readonly studyExpenses$ = this.study$.pipe(
        filter(Boolean),
        switchMap((study) => this.getStudyExpenses(study.id))
    );
    public readonly studyActiveExpenses$ = this.studyExpenses$.pipe(
        map((studyExpenses) => studyExpenses.filter((e) => e.active)),
        shareReplay(1)
    );
    public readonly studyActiveExpensesAsMdsOptions$ = this.studyActiveExpenses$.pipe(
        map((studyExpenses) => {
            return studyExpenses.map((expense) => {
                return <MdsOptionGeneric<StudyExpense>>{
                    value: expense,
                    viewValue: expense.expenseDefaultValue,
                };
            });
        }),
        shareReplay(1)
    );

    private readonly users = new ReplaySubject<User[]>();
    public readonly users$ = this.users.asObservable();

    private readonly expensesSubject = new BehaviorSubject<StudyExpense[] | null>(null);
    private readonly studyLoadTrigger$ = new Subject<number>();

    private activeLoading = false;

    getAvailableExpenses(): Observable<StudyExpense[]> {
        if (this.expensesSubject.getValue() == null && this.activeLoading === false) {
            this.activeLoading = true;
            this.expenseRepository
                .getAvailableExpenses()
                .pipe(
                    tap((expenses) => this.expensesSubject.next(expenses)),
                    catchError((error) => {
                        this.activeLoading = false;
                        throw error;
                    })
                )
                .subscribe();
        }

        return this.expensesSubject.asObservable().pipe(filter(Boolean));
    }
    getStudyActiveExpenses(): Observable<MdsOptionGeneric<StudyExpense>[]> {
        return this.getStudy().pipe(
            map((study) =>
                study.studyInfo.expenseTypes
                    .filter((expense) => expense.active)
                    .map((expense) => {
                        return <MdsOptionGeneric<StudyExpense>>{
                            value: expense,
                            viewValue: expense.expenseDefaultValue,
                        };
                    })
            ),
            shareReplay(1)
        );
    }

    private getStudyExpenses(studyId: number): Observable<StudyExpense[]> {
        return forkJoin({
            availableExpenses: this.getAvailableExpenses().pipe(take(1)),
            studyExpenses: this.studyServices.getStudyExpenseTypes(studyId),
        }).pipe(
            map(({ availableExpenses, studyExpenses }) => {
                let studyExpensesIds = studyExpenses.map((x) => x.expenseId);
                let toAdd = availableExpenses.filter((x) => !studyExpensesIds.includes(x.expenseId));
                if (toAdd) studyExpenses = studyExpenses.concat(toAdd);

                return studyExpenses;
            })
        );
    }

    public setStudy(id: number): void {
        this.studyLoadTrigger$.next(id);
    }
    public setStudy$(id: number) {
        return this.setStudyAsync(id);
    }

    public setStudyAsync(id: number): Observable<Study> {
        let visits = this.studyServices.getStudyVisitTypes(id);

        let regions = this.studyServices.getStudyRegions(id);
        let countries = this.studyServices.getStudyCountries(id);
        let travelOptions = this.studyServices.getStudyTravelOptions(id);
        let sites = this.studyServices.getStudySites(id);
        let study = this.studyServices.getStudy(id);
        return forkJoin([study, visits, this.getStudyExpenses(id), regions, sites, travelOptions, countries]).pipe(
            take(1),
            map(([study, visitTypes, expenseTypes, studyRegions, sites, studyTravelOptions, countries]) => {
                let studyResult = study;
                studyResult.studyInfo.visitTypes = visitTypes;
                studyResult.studyInfo.studyRegions = studyRegions;
                studyResult.studyInfo.studyCountries = countries;
                studyResult.studyInfo.expenseTypes = expenseTypes;
                studyResult.sites = sites;
                studyResult.studyInfo.studyTravelOptions = studyTravelOptions;
                StudyStateService._staticStudy = studyResult;
                this.study.next(studyResult);
                return studyResult;
            })
        );
    }

    public getStudy(): Observable<Study> {
        return this.study$;
    }

    public setNewStudy(newStudy: Study): void {
        StudyStateService._staticStudy = newStudy;
        this.study.next(newStudy);
    }

    public clearStore() {
        StudyStateService._staticStudy = null;
        this.study.next(null);
    }

    public setUsers(id: number): void {
        this.studyServices
            .getStudyUsers(id)
            .pipe(take(1))
            .subscribe((res) => {
                res.forEach((u) => {
                    u['displayName'] = `${u.firstName} ${u.lastName}`;
                });
                return this.users.next(res);
            });
    }

    public getUsers(): Observable<User[]> {
        return this.users$;
    }

    public updateStudy(newStudyData: Study) {
        let mergedStudy = { ...StudyStateService._staticStudy, ...newStudyData };
        this.study.next(mergedStudy);
    }
}
